import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import {
  Loader2,
  Save,
  Sparkles,
  ImageIcon,
  Store as StoreIcon,
  RefreshCcw,
  Globe,
  MessageCircle,
} from "lucide-react";
import storeProfileApi, {
  CoverResult,
  StoreProfilePayload,
} from "../../api/storeProfile";

/**
 * Store profile & cover.
 *
 * One page where a merchant sees their boutique, edits its identity and order
 * details, and generates a professional cover banner with SPEAKON AI. The cover
 * is generated from the store's FULL information — name, city, category,
 * business lines, real product names, price range and its own description — so
 * the result looks like their shop rather than a generic template.
 */

const CATEGORY_OPTIONS = [
  { key: "GENERAL", label: "Commerce général" },
  { key: "FASHION", label: "Mode / Vêtements" },
  { key: "ELECTRONICS", label: "Électronique" },
  { key: "GROCERY", label: "Alimentation" },
  { key: "GYM", label: "Salle de sport" },
  { key: "CAR_BOUTIQUE", label: "Auto / Voitures" },
  { key: "REAL_ESTATE", label: "Immobilier" },
  { key: "SERVICE_BUSINESS", label: "Services" },
];

const COVER_STYLES = [
  { id: "", label: "Automatique" },
  { id: "moderne", label: "Moderne" },
  { id: "chaleureux", label: "Chaleureux" },
  { id: "premium", label: "Premium" },
  { id: "colore", label: "Coloré" },
];

export default function StoreProfilePage() {
  const userdata = useSelector((state: any) => state.UserReducer.userdata);
  const storeId = userdata?.store?._id;

  const [form, setForm] = useState({
    name: "",
    city: "",
    address: "",
    description: "",
    category: "GENERAL",
    publicOrderContactName: "",
    publicOrderWhatsappNumber: "",
    publicOrderInstructions: "",
  });
  const [original, setOriginal] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [coverStyle, setCoverStyle] = useState("");
  const [generating, setGenerating] = useState(false);
  const [cover, setCover] = useState<{ url: string; key: string } | null>(null);
  const [coverCopy, setCoverCopy] = useState<CoverResult["copy"]>(null);
  const [coverError, setCoverError] = useState("");

  const dirty = useMemo(() => {
    if (!original) return false;
    return JSON.stringify(form) !== JSON.stringify(original);
  }, [form, original]);

  const load = useCallback(async () => {
    if (!storeId) return;
    setLoading(true);
    setError("");
    try {
      const [profileData, store] = await Promise.all([
        storeProfileApi.profile(storeId).catch(() => null),
        Promise.resolve(userdata?.store),
      ]);

      setProfile(profileData);

      if (store) {
        const next = {
          name: store.name || "",
          city: store.city || "",
          address: store.address || "",
          description: store.description || "",
          category: store.category || "GENERAL",
          publicOrderContactName: store.publicOrderContactName || "",
          publicOrderWhatsappNumber: store.publicOrderWhatsappNumber || "",
          publicOrderInstructions: store.publicOrderInstructions || "",
        };
        setForm(next);
        setOriginal(next);
        if (store.coverImageUrl) {
          setCover({ url: store.coverImageUrl, key: store.coverImageKey || "" });
        }
      }
    } catch (e: any) {
      setError(e?.message || "Impossible de charger la boutique.");
    } finally {
      setLoading(false);
    }
  }, [storeId, userdata?.store]);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (key: keyof typeof form, value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    if (!storeId) return;
    if (!form.name.trim()) {
      toast.error("Le nom de la boutique est requis.");
      return;
    }
    setSaving(true);
    try {
      const payload: StoreProfilePayload = { ...form };
      await storeProfileApi.update(storeId, payload);
      setOriginal(form);
      toast.success("Boutique mise à jour");
    } catch (e: any) {
      toast.error(e?.message || "Enregistrement impossible");
    } finally {
      setSaving(false);
    }
  };

  const generateCover = async () => {
    if (!storeId) return;
    setGenerating(true);
    setCoverError("");
    setCoverCopy(null);
    try {
      const result = await storeProfileApi.generateCover(storeId, coverStyle || undefined);
      setCover({
        url: result.cover.imageUrl,
        key: result.cover.imageKey || "",
      });
      setCoverCopy(result.copy);
      toast.success("Nouvelle couverture générée");
    } catch (e: any) {
      setCoverError(e?.message || "Génération impossible");
    } finally {
      setGenerating(false);
    }
  };

  const inputClass =
    "mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-cyan-400 focus:ring-2 focus:ring-cyan-100";
  const labelClass = "text-xs font-semibold uppercase tracking-wide text-slate-500";

  if (!storeId) {
    return (
      <div className="rounded-3xl border border-slate-200 bg-white p-8 text-sm text-slate-600 shadow-sm">
        Aucune boutique active.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded-3xl border border-slate-200 bg-white p-8 text-sm text-slate-500">
        <Loader2 size={16} className="animate-spin" />
        Chargement de la boutique…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Ma boutique</h1>
          <p className="mt-1 text-sm text-slate-500">
            Identité, informations de commande et couverture de votre vitrine publique.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <RefreshCcw size={15} />
          Recharger
        </button>
      </div>

      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      {/* ---------------------------------------------- identity + orders */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
          <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <StoreIcon size={16} className="text-cyan-600" />
            Identité de la boutique
          </p>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className={labelClass} htmlFor="store-name">
                Nom *
              </label>
              <input
                id="store-name"
                className={inputClass}
                value={form.name}
                onChange={(event) => setField("name", event.target.value)}
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="store-city">
                Ville
              </label>
              <input
                id="store-city"
                className={inputClass}
                value={form.city}
                onChange={(event) => setField("city", event.target.value)}
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="store-category">
                Type d'activité
              </label>
              <select
                id="store-category"
                className={inputClass}
                value={form.category}
                onChange={(event) => setField("category", event.target.value)}
              >
                {CATEGORY_OPTIONS.map((option) => (
                  <option key={option.key} value={option.key}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass} htmlFor="store-address">
                Adresse
              </label>
              <input
                id="store-address"
                className={inputClass}
                value={form.address}
                onChange={(event) => setField("address", event.target.value)}
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass} htmlFor="store-description">
                Description publique
              </label>
              <textarea
                id="store-description"
                className={`${inputClass} min-h-[90px] resize-none`}
                value={form.description}
                onChange={(event) => setField("description", event.target.value)}
                placeholder="Présentez votre boutique aux visiteurs de votre vitrine."
              />
            </div>
          </div>

          <p className="pt-2 text-sm font-semibold text-slate-800">Réception des commandes</p>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className={labelClass} htmlFor="order-contact">
                Contact de commande
              </label>
              <input
                id="order-contact"
                className={inputClass}
                value={form.publicOrderContactName}
                onChange={(event) => setField("publicOrderContactName", event.target.value)}
              />
            </div>
            <div>
              <label className={labelClass} htmlFor="order-whatsapp">
                Numéro WhatsApp
              </label>
              <input
                id="order-whatsapp"
                className={inputClass}
                value={form.publicOrderWhatsappNumber}
                onChange={(event) => setField("publicOrderWhatsappNumber", event.target.value)}
                placeholder="2250701020304"
                inputMode="tel"
              />
            </div>
            <div className="sm:col-span-2">
              <label className={labelClass} htmlFor="order-instructions">
                Consignes de commande
              </label>
              <textarea
                id="order-instructions"
                className={`${inputClass} min-h-[70px] resize-none`}
                value={form.publicOrderInstructions}
                onChange={(event) => setField("publicOrderInstructions", event.target.value)}
                placeholder="Ex : livraison à Abidjan sous 24h, paiement à la livraison."
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={save}
              disabled={saving || !dirty}
              className="inline-flex items-center gap-2 rounded-xl bg-cyan-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
              Enregistrer
            </button>
            {dirty ? (
              <span className="text-xs text-amber-700">Modifications non enregistrées</span>
            ) : (
              <span className="text-xs text-slate-400">Tout est à jour</span>
            )}
          </div>
        </div>

        {/* --------------------------------------------- what you sell */}
        <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
            <Globe size={16} className="text-cyan-600" />
            Votre vitrine
          </p>

          {profile?.businessLines?.length ? (
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-wide text-slate-500">Vous vendez</p>
              <ul className="space-y-1">
                {profile.businessLines.map((line: any) => (
                  <li key={line.key} className="text-sm text-slate-700">
                    • {line.label}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {profile?.productTypes?.length ? (
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500">Types de produits</p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {profile.productTypes.slice(0, 8).map((type: any) => (
                  <span
                    key={type.type}
                    className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600"
                  >
                    {type.label}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {form.publicOrderWhatsappNumber ? (
            <div className="rounded-xl bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
              <MessageCircle size={12} className="mr-1 inline" />
              Les visiteurs pourront vous contacter sur WhatsApp au {form.publicOrderWhatsappNumber}.
            </div>
          ) : (
            <div className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Ajoutez un numéro WhatsApp pour recevoir les commandes de votre vitrine.
            </div>
          )}
        </div>
      </div>

      {/* --------------------------------------------------- cover studio */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="flex items-center gap-2 text-sm font-semibold text-slate-800">
              <ImageIcon size={16} className="text-cyan-600" />
              Couverture de la vitrine
            </p>
            <p className="mt-1 text-xs text-slate-500">
              SPEAKON AI génère une bannière à partir de toutes les informations de votre
              boutique : nom, ville, activité, articles vendus et fourchette de prix.
            </p>
          </div>

          <div className="flex items-end gap-2">
            <div>
              <label className={labelClass} htmlFor="cover-style">
                Style
              </label>
              <select
                id="cover-style"
                className="mt-1 rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-sm text-slate-900 outline-none focus:border-cyan-400"
                value={coverStyle}
                onChange={(event) => setCoverStyle(event.target.value)}
                disabled={generating}
              >
                {COVER_STYLES.map((style) => (
                  <option key={style.id} value={style.id}>
                    {style.label}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={generateCover}
              disabled={generating}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {generating ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Sparkles size={16} />
              )}
              {generating ? "Génération…" : "Générer avec SPEAKON AI"}
            </button>
          </div>
        </div>

        {coverError ? (
          <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            {coverError}
          </div>
        ) : null}

        {/* cover preview */}
        <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-slate-50">
          <CoverPreview cover={cover} />
        </div>

        {coverCopy ? (
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            {coverCopy.headline ? (
              <div className="rounded-xl bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Titre</p>
                <p className="mt-0.5 text-sm font-semibold text-slate-900">{coverCopy.headline}</p>
              </div>
            ) : null}
            {coverCopy.subtitle ? (
              <div className="rounded-xl bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Accroche</p>
                <p className="mt-0.5 text-sm text-slate-700">{coverCopy.subtitle}</p>
              </div>
            ) : null}
            {coverCopy.callToAction ? (
              <div className="rounded-xl bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Appel à l'action</p>
                <p className="mt-0.5 text-sm font-semibold text-cyan-800">
                  {coverCopy.callToAction}
                </p>
              </div>
            ) : null}
          </div>
        ) : null}

        <p className="mt-3 text-xs text-slate-400">
          Conseil : la couverture est régénérée entièrement à chaque fois. Changez le style si
          vous voulez une autre ambiance.
        </p>
      </div>
    </div>
  );
}

/** Show the current cover, loading state, or an empty-state prompt. */
function CoverPreview({
  cover,
}: {
  cover: { url: string; key: string } | null;
}) {
  const [preview, setPreview] = useState<string>("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!cover?.key) {
      setPreview(cover?.url || "");
      return;
    }

    // The bucket is private: a raw S3 URL is 403 in a browser, so ask the
    // backend for a presigned read URL.
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const response = await apiGetPresigned(cover.key);
        if (!cancelled) setPreview(response);
      } catch {
        if (!cancelled) setPreview(cover.url);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cover?.key, cover?.url]);

  if (loading) {
    return (
      <div className="flex aspect-[16/6] items-center justify-center text-sm text-slate-400">
        <Loader2 size={18} className="animate-spin" />
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="flex aspect-[16/6] flex-col items-center justify-center gap-2 text-center text-sm text-slate-400">
        <ImageIcon size={22} />
        Aucune couverture pour le moment. Générez-en une ci-dessus.
      </div>
    );
  }

  return (
    <img
      src={preview}
      alt="Couverture de la boutique"
      className="aspect-[16/6] w-full object-cover"
    />
  );
}

/**
 * Presigned read URL for a store cover object.
 * Delegates to storeProfileApi so the dynamic import (and its typing) lives in
 * one place.
 */
async function apiGetPresigned(imageKey: string): Promise<string> {
  return storeProfileApi.readCover(imageKey);
}